
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { PlusCircle } from "lucide-react";
import { AddCommentModal } from "@/components/candidates/AddCommentModal";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface MatchCommentsProps {
	matchId: string | undefined;
}

interface MatchComment {
	id: string;
	content: string;
	created_by: string;
	created_at: string;
	updated_at: string;
}

export const MatchComments = ({ matchId }: MatchCommentsProps) => {
	const { t } = useTranslation();
	const [comments, setComments] = useState<MatchComment[]>([]);
	const [loading, setLoading] = useState(true);
	const [isAddCommentOpen, setIsAddCommentOpen] = useState(false);

	const fetchComments = async () => {
		if (!matchId) return;

		try {
			setLoading(true);
			const { data, error } = await supabase
				.from('match_comments')
				.select('*')
				.eq('match_id', matchId)
				.order('created_at', { ascending: false });

			if (error) throw error;

			setComments(data || []);
		} catch (error) {
			console.error('Error fetching match comments:', error);
			toast({
				title: "Error",
				description: "Could not load comments",
				variant: "destructive",
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchComments();
	}, [matchId]);

	const handleAddComment = async (content: string) => {
		if (!matchId || !content) return;

		try {
			// In a real app, get the user's name from auth context
			const userName = "Current User"; // Placeholder

			const { data, error } = await supabase
				.from('match_comments')
				.insert({
					match_id: matchId,
					content,
					created_by: userName,
				})
				.select();

			if (error) throw error;

			toast({
				title: "Success",
				description: "Comment added successfully",
			});

			// Refresh comments
			fetchComments();
		} catch (error) {
			console.error('Error adding comment:', error);
			toast({
				title: "Error",
				description: "Could not add comment",
				variant: "destructive",
			});
		} finally {
			setIsAddCommentOpen(false);
		}
	};

	return (
		<div>
			<div className="flex justify-between items-center mb-4">
				<h3 className="text-lg font-semibold">{t("matches.comments.title", "Comments")}</h3>
				<Button 
					onClick={() => setIsAddCommentOpen(true)}
					className="flex items-center gap-1"
				>
					<PlusCircle className="h-4 w-4" />
					<span>{t("matches.comments.addComment", "Add Comment")}</span>
				</Button>
			</div>

			{loading ? (
				<div className="flex justify-center p-8">
					<div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
				</div>
			) : (
				<>
					{comments.length === 0 ? (
						<p className="text-gray-500 italic text-center py-8">{t("matches.comments.noComments", "No comments yet")}</p>
					) : (
						<div className="space-y-4">
							{comments.map((comment) => (
								<div key={comment.id} className="bg-gray-50 p-4 rounded-lg">
									<div className="flex justify-between mb-2">
										<span className="font-medium">{comment.created_by}</span>
										<span className="text-sm text-gray-500">
											{new Date(comment.created_at).toLocaleString()}
										</span>
									</div>
									<div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: comment.content }} />
								</div>
							))}
						</div>
					)}
				</>
			)}

			<AddCommentModal
				isOpen={isAddCommentOpen}
				onClose={() => setIsAddCommentOpen(false)}
				onSave={handleAddComment}
			/>
		</div>
	);
};
